import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { getMyProfile } from '@/features/profile/api';
import { ProfileDetailsCard } from '@/features/profile/ProfileDetailsCard';
import { useMe } from '@/features/me/MeContext';
import { useAsync } from '@/lib/useAsync';

/**
 * `/profile` (§ Accountmenu › 1): name, nickname and the private fields.
 * Language, region, statistics timezone and theme all live on the Voorkeuren
 * tab instead (`PreferencesPage`); Consents and the subscription have their
 * own tabs too, under the Profiel left menu (`ProfileNav`). The caller's
 * roles (previously badges next to the name in the account dropdown) render
 * inside the Gegevens card, between the general and private fields.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const profile = useAsync(() => getMyProfile(), []);

  const roleBadges: string[] = [];
  if (permissions.isAdmin) roleBadges.push(t('accountMenu.roles.admin'));
  if (permissions.administeredCompanyIds.length > 0)
    roleBadges.push(t('accountMenu.roles.companyAdmin'));
  if (permissions.administeredDepartmentIds.length > 0) {
    roleBadges.push(t('accountMenu.roles.departmentAdmin'));
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('profile.title')} lead={t('profile.lead')} />

      <Card>
        <CardHeader title={t('profile.details.title')} />
        <AsyncSection state={profile} label={t('profile.details.title')} skeleton="card">
          {(user) => <ProfileDetailsCard user={user} roles={roleBadges} />}
        </AsyncSection>
      </Card>
    </section>
  );
}
